"use client";

import {
  AnalyticsActions,
  AnalyticsCategories,
  AnalyticsEventType,
  AnalyticsLabels,
} from "@/constants/analytics";
import { ResetPassword } from "@/http/LiveHttpService";
import { Modal } from "@/components/Util/Modal";
import { sendGTMEvent } from "@/components/Tracking/TagManagerWrapper";
import { useLocaleContext, useTranslation } from "@/app/i18n/client";
import { makeZodI18nMap } from "@daubit/zod-i18n-map";
import { z } from "zod/v4";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputFormField } from "@/app/[lng]/_components/Forms/input-form-field";
import { FormField } from "@/components/ui/form";
import { MdPerson } from "react-icons/md";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";

const zodForgotPasswordData = z.object({
  username: z.string().min(3),
});

export function ForgotPasswordForm() {
  const { t } = useTranslation(["common", "zod", "zodCustomErrors"]);

  z.config(
    makeZodI18nMap({
      t: t,
      ns: ["zod", "zodCustomErrors"],
    }),
  );

  const formMethods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(zodForgotPasswordData),
  });

  const { isPending, isSuccess, mutate } = useMutation({
    mutationFn: async (values: z.infer<typeof zodForgotPasswordData>) => {
      const startTime = new Date().getTime();
      const result = await ResetPassword(values.username);
      const endTime = new Date().getTime();
      const timeDifference = endTime - startTime;
      sendGTMEvent(
        AnalyticsEventType.Timing,
        AnalyticsCategories.forgotPassword,
        AnalyticsLabels.ResetPassword,
        AnalyticsActions.sendData,
        { value: String(timeDifference) },
      );

      if (result.successful) {
        sendGTMEvent(
          AnalyticsEventType.Event,
          AnalyticsCategories.forgotPassword,
          AnalyticsLabels.user,
          AnalyticsActions.formSubmitted,
        );

        return result.entity;
      }
      throw new Error(result.error);
    },
  });

  async function onSubmit(
    values: z.infer<typeof zodForgotPasswordData>,
  ): Promise<void> {
    console.log("Triggered onSubmit");
    sendGTMEvent(
      AnalyticsEventType.Event,
      AnalyticsCategories.forgotPassword,
      AnalyticsLabels.user,
      AnalyticsActions.forgotPassword,
    );

    sendGTMEvent(
      AnalyticsEventType.Event,
      AnalyticsCategories.forgotPassword,
      AnalyticsLabels.button,
      AnalyticsActions.submitFormClick,
    );

    mutate(values);
  }

  const {
    handleSubmit,
    formState: { errors, isValid },
  } = formMethods;

  const router = useRouter();
  const lng = useLocaleContext();

  return (
    <FormProvider {...formMethods}>
      <form
        className="form m-4 border border-solid border-matchbus bg-base"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="form-title mb-0 pb-4">
          {t("SIGNUP.MAIN.RESETPASSWORD.TEXT")}
        </div>
        <div className="table-row-mb flex flex-wrap items-stretch justify-between">
          <FormField
            control={formMethods.control}
            name="username"
            render={({ field }) => (
              <InputFormField
                {...field}
                label={`${t("SIGNUP.MAIN.USERNAME.TEXT")} / ${t("MAIN.EMAIL.TEXT")}`}
                icon={
                  <div className="icon">
                    <MdPerson />
                  </div>
                }
                error={errors.username?.message}
              />
            )}
          />
        </div>
        <Button type="submit" className="w-full" disabled={isPending}>
          {t("SIGNUP.MAIN.RESETPASSWORD.TEXT")}
        </Button>
      </form>
      <Modal
        header={t("MODAL.TITLE.RESETPW")}
        open={isSuccess}
        changedOpen={(open) => {
          if (!open && isSuccess) {
            router.push(`/${lng}/login`);
          }
        }}
      >
        <div>{t("MODAL.RESETPW.TEXT")}</div>
      </Modal>
    </FormProvider>
  );
}
